import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { getGroupById } from "../api/group";
import { useTimer } from "../hooks/useTimer";
import { setGroup as setGroupAction, updateTime } from "../redux/actions";
import { GAME_TIME_SECS, ROUTES } from "../utils/constants";
import {
	formatMinutes,
	formatSeconds,
	formatCapitalMillions,
	getTimer,
	getScenarioNumByPath,
	getGroupId,
	updateStats,
	updateTimer,
	setLastTickNow,
	getLastTick,
	setLastTick,
} from "../utils/helpers";
import { IconInfoCircle, IconBook2, IconLifebuoy } from "@tabler/icons-react";
import Swal from "sweetalert2";
import { modalIntro, modalText, modalSpecialistPick, modalSpecialistInfo, modalSpecialistConfirm } from "../utils/logger";
import {
	SPECIALIST_PROFILES,
	SPECIALIST_TOTAL_USES,
	getSpecialistAvatarSrc,
	getSpecialistHelpText,
	loadLocalSpecialistHelp,
	mergeSpecialistHelp,
	saveLocalSpecialistHelp,
} from "../utils/specialistHelp";
import { buildSpecialistHelpAvatarHtml } from "../utils/quizFeedbackHtml";
import "./TopBar.css";
import PoweredByOwius from "./PoweredByOwius";

const EDUCATIONAL_DISCLAIMER =
	"Trabajo elaborado en el marco del programa ESADE-Cobega y tiene fines exclusivamente educativos";

function isLikelyMongoObjectId(id) {
	return typeof id === "string" && /^[a-f0-9]{24}$/i.test(id);
}

export default function TopBar() {
	const timeout = GAME_TIME_SECS;
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const location = useLocation();
	const game = useSelector((state) => state.game);
	const group = useSelector((state) => state.group);
	const score = group?.score ?? 0;
	const activeScenarioNum = getScenarioNumByPath(location.pathname);
	/** Tras tiempo global agotado, esc. 5 / supertest / escena final siguen en juego: no forzar /gameover. */
	const skipForcedGameover =
		activeScenarioNum === 5 ||
		activeScenarioNum === 6 ||
		activeScenarioNum === 7;

	const groupId = group?._id || getGroupId();
	const localSpecialist = loadLocalSpecialistHelp(groupId);
	const specialistState = mergeSpecialistHelp(group?.specialistHelp, localSpecialist, SPECIALIST_TOTAL_USES);
	const specialistUsesLeft = specialistState.usesLeft;
	const specialistUsed = specialistState.used || {};

	const canUseSpecialist = specialistUsesLeft > 0;

	const specialistAvatarTheme =
		activeScenarioNum === 3
			? "violet"
			: activeScenarioNum === 4
				? "blue"
				: activeScenarioNum === 5
					? "green"
					: "orange";

	const specialistAvatarHtml = useCallback(
		(profileId) =>
			buildSpecialistHelpAvatarHtml(getSpecialistAvatarSrc(profileId), specialistAvatarTheme),
		[specialistAvatarTheme]
	);

	const [time, setTime] = useState(() => {
		const saved = getTimer();
		const n = saved !== null ? Number(saved) : parseInt(group?.time || 0, 10);
		return Number.isFinite(n) ? n : 0;
	});

	useTimer(() => {
		handleTimer();
	}, 1000);

	useEffect(() => {
		if (getTimer() !== null && !getLastTick()) setLastTickNow();
	}, []);

	/**
	 * Desde /start-game4 (y similares) se puede llegar a /scenarios sin pasar por
	 * scenario1ProceedToScenariosBoard, que es quien suele llamar a updateTimer.
	 * Sin clave `total` en localStorage, handleTimer no avanza hasta recargar.
	 */
	useEffect(() => {
		if (location.pathname !== ROUTES.TRANSITION_SCENARIOS) return;
		if (!group?._id) return;
		if (getTimer() !== null) return;
		const seed = Number(group?.time ?? 0);
		const t = Number.isFinite(seed) ? seed : 0;
		updateTimer(t);
		setLastTickNow();
		setTime(t);
	}, [location.pathname, group?._id, group?.time]);

	const handleTimer = () => {
		if (getTimer() === null) return;
		const now = Date.now();
		const prevTick = getLastTick() ?? now;
		const deltaMs = now - prevTick;
		const deltaSec = Math.floor(deltaMs / 1000);
		if (deltaSec <= 0) return;
		setTime((prev) => {
			const next = prev + deltaSec;
			updateTimer(next);
			setLastTick(prevTick + deltaSec * 1000);
			return next;
		});
	};

	useEffect(() => {
		const onVis = () => {
			if (document.visibilityState === "visible") {
				handleTimer();
			}
		};
		document.addEventListener("visibilitychange", onVis);
		return () => document.removeEventListener("visibilitychange", onVis);
	}, []);

	useEffect(() => {
		if (
			time >= timeout &&
			group?.lastVisited !== ROUTES.GAMEOVER &&
			!game?.demo &&
			!skipForcedGameover
		) {
			dispatch(updateTime(time));
			updateStats({
				...group,
				time: time,
				lastVisited: ROUTES.GAMEOVER,
				finished: true,
			});
			navigate(ROUTES.GAMEOVER);
		} else if (group?.finished) {
			const target = group?.lastVisited || ROUTES.RESULTS;
			if (location.pathname !== target) {
				navigate(target);
			}
		}
	}, [time, group, game, navigate, dispatch, location.pathname, skipForcedGameover]);

	useEffect(() => {
		// Rehidratar grupo (si hay refresh) usando localStorage.groupId.
		const id = group?._id || getGroupId();
		if (!id || !isLikelyMongoObjectId(String(id))) {
			return;
		}
		getGroupById(id)
			.then((res) => {
				if (res.status === 200) {
					const g = res.data;
					// Si backend no persiste specialistHelp, lo reinyectamos desde localStorage
					const local = loadLocalSpecialistHelp(id);
					const mergedHelp = mergeSpecialistHelp(g?.specialistHelp, local, SPECIALIST_TOTAL_USES);
					g.specialistHelp = mergedHelp;
					// Mantener redux sincronizado con backend (score + specialistHelp, etc.)
					dispatch(setGroupAction(g));
				}
			})
			.catch(() => {});
	}, [location.pathname, dispatch, group?._id]);

	const handleSpecialist = async () => {
		if (!group?._id) return;

		const scenarioKey = String(activeScenarioNum || 0);
		const usedInScenario = specialistUsed?.[scenarioKey] || {};

		const optionsHtml = `
			<div class="w-100" style="text-align:left;">
				<div class="txtTexto1 mb-3">Elige un perfil (una consulta por perfil y escenario). Coste en puntos.</div>
				<div class="specialist-picker">
					${SPECIALIST_PROFILES.map((p) => {
						const already = Boolean(usedInScenario?.[p.id]);
						const canOpen = already || canUseSpecialist;
						return `
							<button type="button" class="specialist-picker__btn ${already ? "is-used" : ""} ${!canOpen ? "is-disabled" : ""}" data-profile="${p.id}" ${!canOpen ? "disabled" : ""}>
								<span class="specialist-picker__label">${p.label}</span>
								<span class="specialist-picker__cost">-${p.cost}</span>
								${already ? `<span class="specialist-picker__note">Usado en este escenario · Pulsa para ver la respuesta</span>` : ""}
							</button>
						`;
					}).join("")}
				</div>
				<div class="txtTexto1 mt-3">
					Consultas restantes en la partida: <b>${specialistUsesLeft}</b>
					${!canUseSpecialist ? `<span class="ms-2" style="opacity:.85;">(no quedan consultas nuevas)</span>` : ""}
				</div>
			</div>
		`;

		await modalSpecialistPick({
			title: "Consultar especialista",
			html: optionsHtml,
			themePopupClass: "modal-sg5--orange",
		});
	};

	// Atajo: delegamos la interacción a un listener global del modal (SweetAlert).
	useEffect(() => {
		const onClick = async (ev) => {
			const btn = ev.target?.closest?.(".specialist-picker__btn[data-profile]");
			if (!btn) return;
			const profileId = btn.getAttribute("data-profile");
			if (!profileId) return;

			const profile = SPECIALIST_PROFILES.find((p) => p.id === profileId);
			if (!profile) return;

			const scenarioKey = String(activeScenarioNum || 0);
			const already = Boolean(specialistUsed?.[scenarioKey]?.[profileId]);
			if (already) {
				Swal.close();
				const helpText = getSpecialistHelpText(activeScenarioNum, profileId);
				await modalSpecialistInfo({
					title: `Especialista · ${profile.label}`,
					html: `${specialistAvatarHtml(profileId)}<div class="txtTexto1" style="text-align:left;">${helpText}</div>`,
					themePopupClass: "modal-sg5--orange modal-sg5--specialist-info",
				});
				return;
			}
			if (!canUseSpecialist) return;

			// Confirmación con coste
			Swal.close();
			const confirm = await modalSpecialistConfirm({
				title: "Confirmar consulta",
				html: `${specialistAvatarHtml(profileId)}<div class="txtTexto1" style="text-align:left;">Vais a consultar a <b>${profile.label}</b>. Coste: <b>-${profile.cost}</b> puntos.</div>`,
				themePopupClass: "modal-sg5--orange",
			});
			if (!confirm?.isConfirmed) return;

			const nextUses = specialistUsesLeft - 1;
			const nextScore = Math.max(0, Number(group?.score ?? 0) - profile.cost);

			const nextGroup = {
				...group,
				score: nextScore,
				specialistHelp: {
					usesLeft: nextUses,
					used: {
						...specialistUsed,
						[scenarioKey]: {
							...(specialistUsed?.[scenarioKey] || {}),
							[profileId]: true,
						},
					},
				},
			};

			try {
				await updateStats(nextGroup);
				saveLocalSpecialistHelp(nextGroup?._id, nextGroup?.specialistHelp);
				dispatch(setGroupAction(nextGroup));
			} catch (e) {
				// si falla persistencia, no bloqueamos la partida
				saveLocalSpecialistHelp(nextGroup?._id, nextGroup?.specialistHelp);
				dispatch(setGroupAction(nextGroup));
			}

			const helpText = getSpecialistHelpText(activeScenarioNum, profileId);
			await modalSpecialistInfo({
				title: `Especialista · ${profile.label}`,
				html: `${specialistAvatarHtml(profileId)}<div class="txtTexto1" style="text-align:left;">${helpText}</div>`,
				themePopupClass: "modal-sg5--orange modal-sg5--specialist-info",
			});
		};

		document.addEventListener("click", onClick);
		return () => document.removeEventListener("click", onClick);
	}, [
		activeScenarioNum,
		canUseSpecialist,
		dispatch,
		group,
		specialistAvatarHtml,
		specialistUsed,
		specialistUsesLeft,
	]);

	const REFERENCIAS = `
	<div class="w-100 px-4" style="text-align: left;">
		<h3 class="black bold mb-4">
			REFERENCIAS BIBLIOGRÁFICAS
		</h3>
		<div class="txtTexto1 black">
			<ul>
				<li>Armstrong AJ, Szmulewitz RZ, Petrylak DP, et al. ARCHES: A Randomized, Phase III Study of Androgen Deprivation Therapy With Enzalutamide or Placebo in Men With Metastatic Hormone-Sensitive Prostate Cancer. J Clin Oncol. 2019 Nov 10;37(32):2974-86. https://doi.org/10.1200/JCO.19.00799.</li>
				<li>Attard G, Agarwal N, Graff JN, et al. Niraparib and abiraterone acetate plus prednisone for HRR-deficient metastatic castration-sensitive prostate cancer: a randomized phase 3 trial. Nat Med. 2025 Dec;31(12):4109-18. https://doi.org/10.1038/s41591-025-03961-8.</li>
				<li>Barbato F, Fendler WP, Rauscher I, et al. PSMA-PET for the assessment of metastatic hormone-sensitive prostate cancer volume of disease. J Nucl Med. 2021 May 14;62(12):1747-50. https://doi.org/10.2967/jnumed.121.262120.</li>
				<li>Chi KN, Agarwal N, Bjartell A, et al.; TITAN Investigators. Apalutamide for Metastatic, Castration-Sensitive Prostate Cancer. N Engl J Med. 2019 Jul 4;381(1):13-24. https://doi.org/10.1056/NEJMoa1903307.</li>
				<li>Chi KN, Chowdhury S, Bjartell A, et al. Apalutamide in Patients With Metastatic Castration-Sensitive Prostate Cancer: Final Survival Analysis of the Randomized, Double-Blind, Phase III TITAN Study. J Clin Oncol. 2021 Jul 10;39(20):2294-303. https://doi.org/10.1200/JCO.20.03488.</li>
				<li>Fizazi K, Foulon S, Carles J, et al.; PEACE-1 investigators. Abiraterone plus prednisone added to androgen deprivation therapy and docetaxel in <i>de novo</i> metastatic castration-sensitive prostate cancer (PEACE-1): a multicentre, open-label, randomised, phase 3 study with a 2 × 2 factorial design. Lancet. 2022 Apr 30;399(10336):1695-707. https://doi.org/10.1056/NEJMoa1704174.</li>
				<li>Fizazi K, Foulon S, Carles J, et al.; PEACE-1 investigators. Abiraterone plus prednisone added to androgen deprivation therapy and docetaxel in <i>de novo</i> metastatic castration-sensitive prostate cancer (PEACE-1): a multicentre, open-label, randomised, phase 3 study with a 2 × 2 factorial design. Lancet. 2022 Apr 30;399(10336):1695-707. https://doi.org/10.1016/S0140-6736(22)00367-1.</li>
				<li>Francini E, Gray KP, Xie W, et al. Time of metastatic disease presentation and volume of disease are prognostic for metastatic hormone sensitive prostate cancer (mHSPC). Prostate. 2018 Sep;78(12):889-95. https://doi.org/10.1002/pros.23645.</li>
				<li>Hofman MS, Lawrentschuk N, Francis RJ, et al.; proPSMA Study Group Collaborators. Prostate-specific membrane antigen PET-CT in patients with high-risk prostate cancer before curative-intent surgery or radiotherapy (proPSMA): a prospective, randomised, multicentre study. Lancet. 2020 Apr 11;395(10231):1208-16. https://doi.org/10.1016/S0140-6736(20)30314-7.</li>
				<li>Hussain M, Tombal B, Saad F, et al. Darolutamide Plus Androgen-Deprivation Therapy and Docetaxel in Metastatic Hormone-Sensitive Prostate Cancer by Disease Volume and Risk Subgroups in the Phase III ARASENS Trial. J Clin Oncol. 2023 Jul 10;41(20):3595-607. https://doi-org.pbidi.unam.mx:2443/10.1200/JCO.23.00041.</li>
				<li>Merseburger AS, Agarwal N, Bhaumik A, et al. Apalutamide plus androgen deprivation therapy in clinical subgroups of patients with metastatic castration-sensitive prostate cancer: A subgroup analysis of the randomised clinical TITAN study. Eur J Cancer. 2023 Nov;193:113290. https://doi-org.pbidi.unam.mx:2443/10.1016/j.ejca.2023.113290.</li>
				<li>Rodriguez-Vida A, et al. P124: Apalutamide for metastatic castration-sensitive prostate cancer: Outcomes in patients with more than 10 bone metastases from the titan final analysis [resumen de sesión de póster]. EMUC24 - 16th European Multidisciplinary Congress on Urological Cancers, Lisboa, Portugal. https://urosource.uroweb.org/resource-centres/EMUC24/261000/abstract.</li>
				<li>Saad F, Vjaters E, Shore N, et al.; ARANOTE Study Investigators. Darolutamide in Combination With Androgen-Deprivation Therapy in Patients With Metastatic Hormone-Sensitive Prostate Cancer From the Phase III ARANOTE Trial. J Clin Oncol. 2024 Dec 20;42(36):4271-81. https://doi-org.pbidi.unam.mx:2443/10.1200/JCO-24-01798.</li>
				<li>Smith MR, Hussain M, Saad F, et al.; ARASENS Trial Investigators. Darolutamide and Survival in Metastatic, Hormone-Sensitive Prostate Cancer. N Engl J Med. 2022 Mar 24;386(12):1132-42. https://doi-org.pbidi.unam.mx:2443/10.1056/NEJMoa2119115.</li>
				<li>Sweeney CJ, Chen YH, Carducci M, et al. Chemohormonal Therapy in Metastatic Hormone-Sensitive Prostate Cancer. N Engl J Med. 2015 Aug 20;373(8):737-46. https://doi.org/10.1056/NEJMoa1503747.</li>
				<li>Sweeney CJ, Martin AJ, Stockler MR, et al.; ENZAMET trial investigators and Australian and New Zealand Urogenital and Prostate Cancer Trials Group. Testosterone suppression plus enzalutamide versus testosterone suppression plus standard antiandrogen therapy for metastatic hormone-sensitive prostate cancer (ENZAMET): an international, open-label, randomised, phase 3 trial. Lancet Oncol. 2023 Apr;24(4):323-34. https://doi.org/10.1016/S1470-2045(23)00063-3.</li>
				<li>Vale CL, Fisher DJ, Godolphin PJ, et al.; STOPCAP Collaboration. Which patients with metastatic hormone-sensitive prostate cancer benefit from docetaxel: a systematic review and meta-analysis of individual participant data from randomised trials. Lancet Oncol. 2023 Jul;24(7):783-97. https://doi.org/10.1016/S1470-2045(23)00230-9.</li>
			</ul>
		</div>
	</div>
	`;

	const progressPct = Math.min(100, (time / timeout) * 100);

	return (
		<header className="top-bar">
			<div className="top-bar__inner">
				<div className="top-bar__left">
					<div className="top-bar__leftGroup">
						<span className="top-bar__team">
							{group?.fullName || "NOMBRE DEL EQUIPO"}
						</span>
						<span className="top-bar__table">
							<span className="top-bar__tableLabel">Equipo nº</span>
							<span className="top-bar__tableNum">{group?.tableNum || 0}</span>
						</span>
					</div>
					<p className="top-bar__disclaimer">{EDUCATIONAL_DISCLAIMER}</p>
				</div>

				<div className="top-bar__midActions">
					<button
						type="button"
						className="top-bar__iconBtn top-bar__iconBtn--help"
						onClick={handleSpecialist}
						title="Consultar especialista"
						aria-label="Consultar especialista"
						disabled={!group?._id}
					>
						<span className="top-bar__helpLives" aria-hidden>
							{Array.from({ length: SPECIALIST_TOTAL_USES }).map((_, idx) => (
								<IconLifebuoy
									key={idx}
									size={24}
									stroke={2}
									className={
										idx >= (SPECIALIST_TOTAL_USES - specialistUsesLeft)
											? "top-bar__helpLife top-bar__helpLife--on"
											: "top-bar__helpLife top-bar__helpLife--off"
									}
									aria-hidden
								/>
							))}
						</span>
					</button>
				</div>

				<div className="top-bar__scoreWrap">
					<div
						className="top-bar__scoreRing"
						aria-label={`Capital acumulado: ${formatCapitalMillions(score)}`}
						title={String(score ?? 0)}
					>
						<span className="top-bar__scoreVal">
							{formatCapitalMillions(score)}
						</span>
					</div>
				</div>

				<div className="top-bar__right">
					<div className="top-bar__rightRow">
						<PoweredByOwius variant="topbar" />
						<button
							type="button"
							className="top-bar__iconBtn"
							onClick={() => modalIntro()}
							title="Instrucciones"
							aria-label="Instrucciones"
						>
							<IconInfoCircle size={18} stroke={2} aria-hidden />
						</button>
						<div className="top-bar__timer">
							<div className="top-bar__digit">
								{formatMinutes(time, timeout, group?.finished)}
							</div>
							<span className="top-bar__colon blink">:</span>
							<div className="top-bar__digit">
								{formatSeconds(time, timeout, group?.finished)}
							</div>
						</div>
					</div>
					<div className="top-bar__progress" aria-hidden>
						<div
							className="top-bar__progressFill"
							style={{ width: `${progressPct}%` }}
						/>
						<div
							className="top-bar__progressHandle"
							style={{ left: `${progressPct}%` }}
						/>
					</div>
				</div>
			</div>
		</header>
	);
}
